import { ANNOTATION_VALUE } from "@/constants/annotation-values"

import type { Annotation } from "@/types/api/Annotation"

export const convertStringToFloat = (
  stringValue: string | null,
): number | null => {
  if (stringValue === null) {
    return null
  }

  switch (stringValue) {
    case ANNOTATION_VALUE.YES:
      return 1
    case ANNOTATION_VALUE.NO:
      return 0
    case ANNOTATION_VALUE.UNKNOWN:
      return -1
    default:
      return null
  }
}

export const createOptimisticAnnotation = (
  simulationOutputId: string,
  metricId: string,
  currentAnnotation: Annotation | null,
  updates: Partial<Annotation>,
): Annotation => ({
  id: `temp-${Date.now()}`,
  simulation_output_id: simulationOutputId,
  metric_id: metricId,
  assignee: currentAnnotation?.assignee || "",
  ground_truth_float_value:
    updates.ground_truth_float_value ??
    currentAnnotation?.ground_truth_float_value ??
    null,
  ground_truth_string_value:
    updates.ground_truth_string_value ??
    currentAnnotation?.ground_truth_string_value ??
    null,
  ground_truth_set_value:
    updates.ground_truth_set_value ??
    currentAnnotation?.ground_truth_set_value ??
    null,
  ground_truth_json:
    updates.ground_truth_json ?? currentAnnotation?.ground_truth_json ?? null,
  ground_truth_subvalues_by_timestamp:
    currentAnnotation?.ground_truth_subvalues_by_timestamp ?? null,
  annotations: {
    ...currentAnnotation?.annotations,
    ...updates.annotations,
  },
  archived: currentAnnotation?.archived ?? false,
  created_at: currentAnnotation?.created_at || new Date().toISOString(),
  last_updated_at: new Date().toISOString(),
  priority: currentAnnotation?.priority || "STANDARD",
})

/**
 * Resolves the effective owner email for an annotation row about to be
 * edited. The collaborative-projects context (`resolvedOwnerEmail`) carries
 * the *real* owner of the merged row currently displayed:
 *   - a non-null email means "this row belongs to that person" — trust it;
 *     this is what makes snapshot-on-edit correct in collab projects where
 *     the displayed row is spoofed to the current user but actually belongs
 *     to another reviewer.
 *   - `null` means "no merged row exists in the context for this metric" —
 *     happens before the post-create refetch lands, when the user just
 *     inserted a row in this session. Fall back to the local assignee
 *     (which reflects the just-created row) so a follow-up edit updates in
 *     place instead of snapshot-creating a duplicate.
 *   - `undefined` means there is no provider mounted at all — fall back to
 *     the local assignee.
 */
export const resolveAnnotationOwnerEmail = (
  localAssignee: string | null,
  resolvedOwnerEmail: string | null | undefined,
): string | null => resolvedOwnerEmail ?? localAssignee

export const setValuesEqual = (
  a: string[] | null | undefined,
  b: string[] | null | undefined,
): boolean => {
  if (!a && !b) {
    return true
  }
  if (!a || !b) {
    return false
  }
  if (a.length !== b.length) {
    return false
  }
  return a.every((v, i) => v === b[i])
}
